// ============================================================================
// PHOTO CLIENT IMAGE POLICY
// ----------------------------------------------------------------------------
// Client-facing quote/PDF photos stay clean unless a person deliberately
// saved a manual markup.
//
// QuoteMaker no longer draws AI markup on photos (removed 2026-10-13), but
// photos saved before then still carry the old AI overlay fields
// (aiMarkupData, aiMarkupRegions, aiAnnotated). This module is the shared
// boundary so hero images, quote thumbnails, and persistence never show
// those overlays.
//
// DEC-COUNCIL-007 / Candidate C6 Slice 2 (cascade collapse): the predicates
// and markup classifiers used here are now sourced from
// `photo-resolution-policy.js` — the same module use-photo-src.js consumes —
// so the two photo-resolution cascades stay in lockstep on field rules.
// ============================================================================

import {
    isObjectPhoto,
    isStrictDataUrl as isDataUrl,
    isManualPhotoMarkup as _isManualPhotoMarkup,
    isAiOnlyPhotoMarkup as _isAiOnlyPhotoMarkup,
} from './photo-resolution-policy.js';

function pickFirstDataUrl(...values: any[]) {
    return values.find(isDataUrl) || null;
}

// Re-exports preserved so existing callers keep working — markup classifiers
// are owned by photo-resolution-policy.js but exposed here for backward-compat.
export const isManualPhotoMarkup = _isManualPhotoMarkup;
export const isAiOnlyPhotoMarkup = _isAiOnlyPhotoMarkup;

export function getCleanPhotoDataUrlSync(photo: unknown) {
    if (typeof photo === 'string') return isDataUrl(photo) ? photo : null;
    if (!isObjectPhoto(photo)) return null;

    if (isManualPhotoMarkup(photo)) {
        return pickFirstDataUrl(photo.data, photo.dataUrl, photo.originalData);
    }

    // Legacy AI-markup photos replaced `data` and kept the pristine base in
    // originalData. New AI-markup photos keep data clean and store overlays in
    // aiMarkupData. Prefer originalData when present so both eras render cleanly.
    if (isAiOnlyPhotoMarkup(photo)) {
        return pickFirstDataUrl(
            photo.originalData,
            photo.clientData,
            photo.cleanData,
            photo.data !== photo.aiMarkupData ? photo.data : null,
            photo.dataUrl !== photo.aiMarkupData ? photo.dataUrl : null,
        );
    }

    return pickFirstDataUrl(photo.data, photo.dataUrl, photo.originalData);
}

export function getClientFacingPhoto(photo: unknown) {
    if (typeof photo === 'string') return { data: photo };
    if (!isObjectPhoto(photo)) return photo;
    if (isManualPhotoMarkup(photo)) return photo;

    const cleanData = getCleanPhotoDataUrlSync(photo);
    if (isAiOnlyPhotoMarkup(photo)) {
        return {
            ...photo,
            data: cleanData || undefined,
            dataUrl: undefined,
            thumbnailUrl: cleanData ? photo.thumbnailUrl : undefined,
        };
    }

    return photo;
}

export async function resolveClientPhotoDataUrl(photo: unknown, expectedProjectId: string | null = null) {
    const clean = getClientFacingPhoto(photo);
    const sync = getCleanPhotoDataUrlSync(clean);
    if (sync) return sync;
    const { resolvePhotoDataUrl } = await import('./photo-auto-assign');
    return resolvePhotoDataUrl(clean, expectedProjectId);
}

export function sanitizeHeroImageForClient(heroImage: unknown, candidatePhotos: any[] = []) {
    if (!heroImage) return heroImage;
    const photos = Array.isArray(candidatePhotos) ? candidatePhotos : [];

    if (isObjectPhoto(heroImage)) {
        return getCleanPhotoDataUrlSync(heroImage) || heroImage;
    }

    if (typeof heroImage !== 'string') return heroImage;

    for (const photo of photos) {
        if (!isObjectPhoto(photo)) continue;
        const cleanData = getCleanPhotoDataUrlSync(photo);
        if (!cleanData) continue;
        const markedCandidates = [
            photo.aiMarkupData,
            photo.data,
            photo.dataUrl,
        ].filter(isDataUrl);
        if (markedCandidates.includes(heroImage) && isAiOnlyPhotoMarkup(photo)) {
            return cleanData;
        }
    }

    return heroImage;
}

export const _internals = Object.freeze({
    isDataUrl,
    isObjectPhoto,
    pickFirstDataUrl,
});
